import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getEnvironmentImportReportById } from './environmentImportApi.js';

const statusConfig = {
  running: { label: 'En cours', color: 'info' },
  success: { label: 'Réussi', color: 'success' },
  partial_success: { label: 'Partiellement réussi', color: 'warning' },
  failed: { label: 'Échec', color: 'error' },
};

const entityLabels = {
  competition: 'Compétitions',
  team: 'Équipes',
  player: 'Joueurs',
  match: 'Matchs',
  gameFact: 'Faits de jeu',
};

const categoryLabels = {
  created: 'Créations',
  updated: 'Modifications',
  replaced: 'Remplacements',
  failed: 'Échecs',
};

const getEntityHref = (entityType, targetId) => {
  if (!targetId) return null;
  if (entityType === 'competition') return `/sports/competitions/create/${targetId}`;
  if (entityType === 'team') return `/sports/teams/create/${targetId}`;
  if (entityType === 'player') return `/sports/players/create/${targetId}`;
  if (entityType === 'match') return `/match/${targetId}`;
  return null;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('fr-FR');
};

const formatDuration = (value) => {
  if (!Number.isFinite(value)) return '—';
  if (value < 1000) return `${value} ms`;
  if (value < 60000) return `${(value / 1000).toFixed(1)} s`;
  const minutes = Math.floor(value / 60000);
  const seconds = Math.round((value % 60000) / 1000);
  return `${minutes} min ${seconds} s`;
};

const actorLabel = (actor) =>
  actor?.email || actor?.username || actor?.name || actor?.id || 'Admin non identifié';

const TechnicalItemList = ({ entityType, items }) => {
  const [visibleCount, setVisibleCount] = useState(100);
  const visible = items.slice(0, visibleCount);

  return (
    <Stack spacing={0.5}>
      {visible.map((item, index) => {
        const href = getEntityHref(entityType, item?.targetId);
        return (
          <Box
            key={`${item?.targetId || item?.sourceId || index}-${index}`}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              py: 0.6,
              '& + &': { borderTop: '1px solid', borderColor: 'divider' },
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ flex: '1 1 280px' }}>
              {item?.name || item?.targetId || item?.sourceId || 'Entité'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item?.provider || '—'} · {item?.targetId || 'sans id cible'}
            </Typography>
            {href && (
              <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                Ouvrir
                <OpenInNewOutlinedIcon fontSize="inherit" />
              </Link>
            )}
            {item?.error && (
              <Alert severity="error" sx={{ width: '100%', mt: 0.5 }}>{item.error}</Alert>
            )}
          </Box>
        );
      })}
      {visibleCount < items.length && (
        <Box sx={{ pt: 1 }}>
          <Button size="small" onClick={() => setVisibleCount((count) => count + 100)}>
            Afficher 100 de plus ({items.length - visibleCount} restant(s))
          </Button>
        </Box>
      )}
    </Stack>
  );
};

const EnvironmentImportReportDetail = () => {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getEnvironmentImportReportById(id);
        if (mounted) setReport(response?.data || null);
      } catch (err) {
        if (mounted) {
          setError(err?.response?.data?.message || err?.message || 'Impossible de charger ce rapport.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [id]);

  const technicalSections = useMemo(() => {
    const details = report?.technicalDetails || {};
    return Object.entries(entityLabels).map(([entityType, label]) => ({
      entityType,
      label,
      data: details?.[entityType] || {},
    }));
  }, [report]);

  if (loading) {
    return <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  if (error || !report) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', py: 3 }}>
        <Stack spacing={2}>
          <Button component={RouterLink} to="/admin/environment-import/reports" startIcon={<ArrowBackOutlinedIcon />} sx={{ width: 'fit-content' }}>
            Retour aux rapports
          </Button>
          <Alert severity="error">{error || 'Rapport introuvable.'}</Alert>
        </Stack>
      </Box>
    );
  }

  const status = statusConfig[report.status] || statusConfig.failed;
  const summary = report.executionSummary || {};

  return (
    <Box sx={{ maxWidth: 1250, mx: 'auto', py: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Button component={RouterLink} to="/admin/environment-import/reports" startIcon={<ArrowBackOutlinedIcon />} sx={{ mb: 1 }}>
            Retour aux rapports
          </Button>
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="h4" component="h1">Rapport d'import</Typography>
            <Chip label={status.label} color={status.color} />
          </Stack>
          <Typography variant="caption" color="text.secondary">ID rapport : {report._id}</Typography>
        </Box>

        {report.error?.message && <Alert severity="error">{report.error.message}</Alert>}

        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Informations générales</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <Box><Typography variant="caption" color="text.secondary">Utilisateur</Typography><Typography fontWeight={600}>{actorLabel(report.actor)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Début</Typography><Typography fontWeight={600}>{formatDate(report.startedAt)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Fin</Typography><Typography fontWeight={600}>{formatDate(report.finishedAt)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Durée</Typography><Typography fontWeight={600}>{formatDuration(report.durationMs)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Provider source</Typography><Typography fontWeight={600}>{report?.source?.provider || '—'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Discipline</Typography><Typography fontWeight={600}>{report?.source?.discipline || '—'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Provider(s) cible(s)</Typography><Typography fontWeight={600}>{(report.targetProviders || []).join(', ') || '—'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Stratégie GameFacts</Typography><Typography fontWeight={600}>{report.gameFactStrategy === 'replace' ? 'Remplacer' : 'Ne pas importer sur les Matchs existants'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Fingerprint</Typography><Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{report.planFingerprint || '—'}</Typography></Box>
            </Box>

            <Divider />

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label={`${summary.created || 0} création(s)`} color="success" variant="outlined" />
              <Chip label={`${summary.updated || 0} modification(s)`} color="warning" variant="outlined" />
              <Chip label={`${summary.unchanged || 0} déjà à jour`} variant="outlined" />
              {(summary.skipped || 0) > 0 && <Chip label={`${summary.skipped || 0} non importé(s)`} variant="outlined" />}
              <Chip label={`${summary.failed || 0} échec(s)`} color={summary.failed ? 'error' : 'default'} variant="outlined" />
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Compétitions et destinations</Typography>
          <Stack spacing={1}>
            {(report.targets || []).map((target, index) => (
              <Box key={`${target.sourceCompetitionId}-${index}`} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography fontWeight={600}>{target.sourceCompetitionName || target.sourceCompetitionId}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {target.sourceCompetitionSeason || 'Saison non renseignée'} → {(target.targetProviders || []).join(', ') || '—'}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Détail technique des écritures</Typography>
          <Stack spacing={1}>
            {technicalSections.map(({ entityType, label, data }) => {
              const categories = ['created', 'updated', 'replaced', 'failed']
                .map((category) => ({ category, items: Array.isArray(data?.[category]) ? data[category] : [] }))
                .filter(({ items }) => items.length > 0);
              const total = categories.reduce((sum, category) => sum + category.items.length, 0);

              if (!total && !(entityType === 'gameFact' && data?.replacementBatches?.length)) return null;

              return (
                <Accordion key={entityType} variant="outlined" disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>{label} · {total} action(s)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1.5}>
                      {categories.map(({ category, items }) => (
                        <Accordion key={`${entityType}-${category}`} variant="outlined" disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" fontWeight={600}>{categoryLabels[category]} ({items.length})</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TechnicalItemList entityType={entityType} items={items} />
                          </AccordionDetails>
                        </Accordion>
                      ))}

                      {entityType === 'gameFact' && Array.isArray(data?.replacementBatches) && data.replacementBatches.length > 0 && (
                        <Accordion variant="outlined" disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" fontWeight={600}>Lots de remplacement ({data.replacementBatches.length})</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              {data.replacementBatches.map((batch, index) => (
                                <Box key={`${batch.matchId}-${index}`} sx={{ p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                                  <Typography variant="body2" fontWeight={600}>{batch.matchName || batch.matchId}</Typography>
                                  <Typography variant="caption" display="block">Provider : {batch.provider || '—'}</Typography>
                                  <Typography variant="caption" display="block">Match cible : {batch.matchId || '—'}</Typography>
                                  <Typography variant="caption" display="block">GF supprimés : {(batch.deletedIds || []).length}</Typography>
                                  <Typography variant="caption" display="block">GF recréés : {(batch.createdIds || []).length}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default EnvironmentImportReportDetail;
